/**
 * list-view-support — JEDNA deklaracja „tej liście systemowej brakuje trybu widoku X".
 *
 * Czytają ją dwa miejsca, żeby nie mogły się rozjechać:
 *  - strona listy (`ConfigurableTable` → `allowedViewModes`) — przez `allowedViewModesForList`,
 *  - Kreator Listy (`SystemModuleListBuilderPage`) — przez `listSupportsViewMode`: zakładka
 *    trybu, którego lista i tak nie pokaże, jest w kreatorze ukryta (nie da się skonfigurować
 *    widoku, którego użytkownik nigdy nie zobaczy).
 *
 * Kluczem jest slug listy systemowej (ten sam, którym kreator otwiera listę, np. `contractors`).
 * Lista bez wpisu obsługuje wszystkie tryby.
 */

import { type ViewMode, viewModeSchema } from '@saas/shared';

/**
 * Tryby widoku, których dana lista systemowa NIE obsługuje.
 *
 * `contractor_contacts` — bez kalendarza: trasa `GET /contractor-contacts` nie jest na silniku
 * list, więc nie ma filtra zakresu miesiąca (ani kolumn dat na tej liście) — kalendarz nie
 * miałby czego narysować. Gdy kontakty trafią na silnik list, wystarczy usunąć ten jeden wpis:
 * zakładka „Kalendarz" wróci w kreatorze, a strona listy odzyska tryb kalendarza. Konfiguracja
 * kalendarza zapisana wcześniej w układzie listy kontaktów u klienta (gdy zakładka jeszcze była
 * dostępna) pozostaje uśpiona i po usunięciu tego wpisu może wrócić do działania.
 *
 * `Map`, nie obiekt: slug pochodzi z trasy/propsów, a obiekt zwróciłby coś dla `constructor`.
 */
const UNSUPPORTED_VIEW_MODES: ReadonlyMap<string, readonly ViewMode[]> = new Map<
  string,
  readonly ViewMode[]
>([['contractor_contacts', ['calendar']]]);

/**
 * Gotowe, referencyjnie STABILNE zbiory dozwolonych trybów per lista (wyliczone raz z deklaracji
 * powyżej) — `ConfigurableTable` ma `allowedViewModes` w zależnościach memo, więc strona nie
 * może dostawać nowej tablicy przy każdym renderze. Tablice są zamrożone (`Object.freeze`) —
 * ta sama referencja trafia do wielu konsumentów, więc przypadkowa mutacja (`push`/`sort`) u
 * jednego popsułaby pozostałych.
 */
const ALLOWED_VIEW_MODES: ReadonlyMap<string, readonly ViewMode[]> = new Map<
  string,
  readonly ViewMode[]
>(
  [...UNSUPPORTED_VIEW_MODES].map(([slug, unsupported]): [string, readonly ViewMode[]] => [
    slug,
    Object.freeze(viewModeSchema.options.filter((mode) => !unsupported.includes(mode))),
  ]),
);

/** Czy lista systemowa o tym slugu obsługuje dany tryb widoku (brak sluga = obsługuje wszystko). */
export function listSupportsViewMode(slug: string | null | undefined, mode: ViewMode): boolean {
  if (!slug) return true;
  return !UNSUPPORTED_VIEW_MODES.get(slug)?.includes(mode);
}

/**
 * Wartość propa `allowedViewModes` dla `ConfigurableTable` danej listy: wszystkie tryby poza
 * nieobsługiwanymi (stała kolejność lista / karty / Kanban / kalendarz). `undefined`, gdy lista
 * niczego nie wyłącza — tabela zachowuje się wtedy jak bez propa.
 */
export function allowedViewModesForList(slug: string): readonly ViewMode[] | undefined {
  return ALLOWED_VIEW_MODES.get(slug);
}
